import {useContext, useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {
    LayerGroup,
    LayersControl,
    MapContainer,
    Marker,
    Popup,
    TileLayer,
    useMapEvents,
    Pane,
    CircleMarker,
    Polygon,
} from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";

import NavBreadcrum from "components/NavBreadcrum";
import navs from "./navs";
import AuthContext from "context/AuthContext";
import MapResizeZoom from "components/maps/MapResizeZoom";
import FitBoundsToCoordinates from "components/maps/FitBoundsToCoordinates";
import axiApi from "ApiCaller";
import {CARTO_TILE_URL, CARTO_TILE_OPTIONS} from 'core/mapsConfig';

const ViewGlobalMap = () => {
    const {Overlay} = LayersControl;
    const {t: _Tr} = useTranslation("routes");
    const {panel} = useContext(AuthContext);
    const [zoom, setZoom] = useState(13);
    const [position, setPosition] = useState(panel.coords);
    const [clickedPosition, setClickedPosition] = useState(null);
    const [mapHeight, setMapHeight] = useState(() => {
        if (typeof window === "undefined") return "800px";
        return `${Math.max(window.innerHeight - 200, 0)}px`;
    });
    document.title = "Mapa Global";
    const bread = [
        {name: _Tr("labels.advanced"), url: "/advanced/", current: false},
        {name: document.title, current: true},
    ];
    const [devices, setDevices] = useState([]);
    const [assets, setAssets] = useState([]);
    const [islands, setIslands] = useState([]);
    const [zones, setZones] = useState([]);

    const deviceSquareIcon = useMemo(
        () =>
            L.divIcon({
                className: "",
                html: '<div style="width:10px;height:10px;background:#1774fa;border:1px solid #ffffff;box-shadow:0 2px 8px 1px rgba(0,0,0,0.25);"></div>',
                iconSize: [10, 10],
                iconAnchor: [5, 5],
            }),
        [],
    );
    const assetIcon = useMemo(
        () =>
            L.divIcon({
                className: "",
                html: '<div style="width:20px;height:20px;border-radius:50%;background:#22c55e;opacity:0.5;border:1px solid #15803d;"></div>',
                iconSize: [20, 20],
                iconAnchor: [10, 10],
            }),
        [],
    );

    const islandIcon = useMemo(
        () =>
            L.divIcon({
                className: "",
                html: '<div style="width:30px;height:30px;border-radius:50%;background:#ad95dd;border:1px solid #7e49df;opacity:.75;"></div>',
                iconSize: [30, 30],
                iconAnchor: [15, 15],
            }),
        [],
    );
    const allCoords = useMemo(() => {
        const coords = [];

        devices.forEach((d) => coords.push([Number(d.lat), Number(d.lng)]));
        assets.forEach((a) => coords.push([Number(a.lat), Number(a.lng)]));
        islands.forEach((i) => coords.push([Number(i.lat), Number(i.lng)]));

        return coords;
    }, [devices, assets, islands]);
    const zoneColors = ["#e6194B", "#3cb44b", "#4363d8", "#f58231", "#911eb4", "#42d4f4"];
    // data table

    useEffect(() => {
        const updateMapHeight = () => setMapHeight(`${Math.max(window.innerHeight - 200, 0)}px`);
        updateMapHeight();

        window.addEventListener("resize", updateMapHeight);
        fetchData();

        return () => window.removeEventListener("resize", updateMapHeight);
    }, []);

    const fetchData = async () => {
        const respDevs = await axiApi.get("/api/devices/locations/");
        if (respDevs.data.status) {
            setDevices(respDevs.data.items);
        }
        const respAssets = await axiApi.get("/api/assets/locations/");
        if (respAssets.data.status) {
            setAssets(respAssets.data.items);
        }
        const respIslands = await axiApi.get("/api/distribution/islands/locations/");
        if (respIslands.data.status) {
            setIslands(respIslands.data.items);
        }
        console.log("GET ZONES");
        const respZones = await axiApi.get("/api/distribution/zones/");
        if (respZones.data) {
            setZones(respZones.data.items);
        }
    };

    const MapClickHandler = () => {
        useMapEvents({
            click(event) {
                setClickedPosition(event.latlng);
                console.log(event.latlng);
            },
            zoomend(event) {
                const newZoom = event.target.getZoom();
                setZoom(newZoom);
            },
        });
        return null;
    };

    return (
        <>
            <NavBreadcrum navs={navs} bread={bread} />
            <div className="row">
                <div className="col-12">
                    <h2>Global Map View</h2>
                    <p>Interactive map displaying global data points and locations.</p>
                </div>
                <div className="col-12">
                    <div className="input-group mb-3" style={{width: "350px"}}>
                        <span className="input-group-text">lat</span>
                        <input
                            type="number"
                            className="form-control"
                            aria-label="Username"
                            value={clickedPosition ? clickedPosition.lat.toFixed(6) : ""}
                            readOnly
                        />
                        <span className="input-group-text">lng</span>
                        <input
                            type="number"
                            className="form-control"
                            aria-label="Server"
                            value={clickedPosition ? clickedPosition.lng.toFixed(6) : ""}
                            readOnly
                        />
                    </div>
                </div>
            </div>
            <MapContainer
                className="mt-2"
                id={Math.random().toString(36).substring(2, 18)}
                center={position}
                zoom={zoom}
                scrollWheelZoom={true}
                style={{width: "100%", height: mapHeight, zIndex: "2"}}>
                {position.length && position[0] && position[1] ? <MapResizeZoom position={position} zoomDefault={zoom} /> : <></>}

                <TileLayer
                    url={CARTO_TILE_URL}
                    {...CARTO_TILE_OPTIONS}
                    maxNativeZoom={20}
                    maxZoom={30}
                />
                <FitBoundsToCoordinates coordinates={allCoords} />
                <Pane name="pane-zones" style={{zIndex: 400}} />
                <Pane name="pane-islands" style={{zIndex: 410}} />
                <Pane name="pane-assets" style={{zIndex: 420}} />
                <Pane name="pane-devices" style={{zIndex: 430}} />
                <Pane name="pane-clicked" style={{zIndex: 440}} />
                <LayersControl position="topright">
                    <Overlay checked name="Devices">
                        <LayerGroup>
                            <MarkerClusterGroup chunkedLoading disableClusteringAtZoom={17}>
                                {devices.map((device, idx) => {
                                    const lat = Number(device.lat);
                                    const lng = Number(device.lng);
                                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                                    return (
                                        <Marker
                                            key={device.uuid || `${lat}-${lng}-${idx}`}
                                            position={[lat, lng]}
                                            icon={deviceSquareIcon}
                                            pane="pane-devices">
                                            <Popup>
                                                <b>{device.uuid}</b>
                                                <br />
                                                {lat.toFixed(6)}, {lng.toFixed(6)}
                                            </Popup>
                                        </Marker>
                                    );
                                })}
                            </MarkerClusterGroup>
                        </LayerGroup>
                    </Overlay>

                    <Overlay checked name="Assets">
                        <LayerGroup>
                            <MarkerClusterGroup chunkedLoading disableClusteringAtZoom={17}>
                                {assets.map((asset, idx) => {
                                    const lat = Number(asset.lat);
                                    const lng = Number(asset.lng);
                                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                                    return (
                                        <Marker
                                            key={asset.uid || `${lat}-${lng}-${idx}`}
                                            position={[lat, lng]}
                                            icon={assetIcon}
                                            pane="pane-assets">
                                            <Popup>
                                                <b>{asset.uid || "Asset"}</b>
                                                <br />
                                                {lat.toFixed(6)}, {lng.toFixed(6)}
                                            </Popup>
                                        </Marker>
                                    );
                                })}
                            </MarkerClusterGroup>
                        </LayerGroup>
                    </Overlay>

                    <Overlay checked name="Islands">
                        <LayerGroup>
                            <MarkerClusterGroup chunkedLoading disableClusteringAtZoom={17}>
                                {islands.map((island, idx) => {
                                    const lat = Number(island.lat ?? island.coords?.[0]);
                                    const lng = Number(island.lng ?? island.coords?.[1]);
                                    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

                                    return (
                                        <Marker
                                            key={island.uid || island.id || island.uuid || island.name || `${lat}-${lng}-${idx}`}
                                            position={[lat, lng]}
                                            icon={islandIcon}
                                            pane="pane-islands">
                                            <Popup>
                                                <b>{island.name || island.uid || island.uuid || "Island"}</b>
                                                <br />
                                                {lat.toFixed(6)}, {lng.toFixed(6)}
                                            </Popup>
                                        </Marker>
                                    );
                                })}
                            </MarkerClusterGroup>
                        </LayerGroup>
                    </Overlay>
                    <Overlay checked name="Zones">
                        <LayerGroup>
                            {zones.map((zone, idx) => {
                                const positions = (zone.polylines ?? [])
                                    .map((p) => [Number(p.lat), Number(p.lng)])
                                    .filter(([lat, lng]) => Number.isFinite(lat) && Number.isFinite(lng));

                                if (positions.length < 2) return null;

                                const color = zoneColors[idx % zoneColors.length];

                                return (
                                    <Polygon
                                        key={zone.id ?? idx}
                                        positions={positions}
                                        pane="pane-zones"
                                        pathOptions={{color: color, weight: 2, fillColor: color, fillOpacity: 0.15}}>
                                        <Popup>
                                            <b>{zone.name}</b>
                                            {zone.description ? (
                                                <>
                                                    <br />
                                                    {zone.description}
                                                </>
                                            ) : null}
                                        </Popup>
                                    </Polygon>
                                );
                            })}
                        </LayerGroup>
                    </Overlay>
                </LayersControl>
                {clickedPosition ? (
                    <CircleMarker
                        center={clickedPosition}
                        radius={5}
                        pathOptions={{color: "#2563eb", weight: 2, fillColor: "#2563eb", fillOpacity: 0.3}}
                        pane="pane-clicked">
                        <Popup>
                            {clickedPosition.lat.toFixed(6)}, {clickedPosition.lng.toFixed(6)}
                        </Popup>
                    </CircleMarker>
                ) : null}
                <MapClickHandler />
            </MapContainer>
        </>
    );
};

export default ViewGlobalMap;
